/**
 * Customers tab component
 * Features:
 * - Recent wins section (5 most recent)
 * - Recent losses section (5 most recent)
 * - Top 10 customers by rank
 * - All customers table with CSV export
 *
 * Uses a single API call (/customers/tab) to fetch all data at once,
 * avoiding 4 separate requests that serialize on the backend.
 */

import { useState, useEffect, useCallback } from 'react';
import { getCustomerTabData, exportCustomersCSV } from '../../../services/api';
import { ConfidenceBadge } from '../../common/ConfidenceIndicator';
import type { Customer, CustomerWin, CustomerLoss, CustomerTabResponse } from '../../../types';

interface CustomersTabProps {
  companyId: string;
}

// Win card component - compact 2-line layout
function WinCard({ win }: { win: CustomerWin }) {
  const date = new Date(win.announcement_date).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });

  return (
    <div
      className="rounded-lg border border-green-200 bg-green-50 px-3 py-2"
      data-testid="customer-win-card"
    >
      {/* Line 1: Icon, Name, Date, Confidence */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-green-600 flex-shrink-0">✓</span>
          <span className="font-medium text-gray-900 truncate">{win.customer_name}</span>
          <span className="text-xs text-gray-500 flex-shrink-0">{date}</span>
        </div>
        <ConfidenceBadge dataQuality={win.data_quality} showLabel={false} />
      </div>
      {/* Line 2: Products */}
      {win.products.length > 0 && (
        <div className="mt-1 ml-5 flex flex-wrap gap-1">
          {win.products.map((product) => (
            <span
              key={product}
              className="inline-block rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-800"
            >
              {product}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Loss card component - compact 2-line layout
function LossCard({ loss }: { loss: CustomerLoss }) {
  const date = new Date(loss.loss_date).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });

  return (
    <div
      className="rounded-lg border border-red-200 bg-red-50 px-3 py-2"
      data-testid="customer-loss-card"
    >
      {/* Line 1: Icon, Name, Date, Competitor, Confidence */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-red-600 flex-shrink-0">✗</span>
          <span className="font-medium text-gray-900 truncate">{loss.customer_name}</span>
          <span className="text-xs text-gray-500 flex-shrink-0">
            {date}
            {loss.competitor_name && <span className="ml-1">→ {loss.competitor_name}</span>}
          </span>
        </div>
        <ConfidenceBadge dataQuality={loss.data_quality} showLabel={false} />
      </div>
      {/* Line 2: Products lost */}
      {loss.products_lost.length > 0 && (
        <div className="mt-1 ml-5 flex flex-wrap gap-1">
          {loss.products_lost.map((product) => (
            <span
              key={product}
              className="inline-block rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-800"
            >
              {product}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Customer row for table
function CustomerRow({ customer, rank }: { customer: Customer; rank?: number }) {
  const firstAnnounced = customer.first_announced
    ? new Date(customer.first_announced).toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
      })
    : '-';

  return (
    <tr className="hover:bg-gray-50" data-testid="customer-row">
      {rank !== undefined && (
        <td className="px-4 py-3 text-center text-sm font-medium text-gray-900">{rank}</td>
      )}
      <td className="px-4 py-3">
        <div className="font-medium text-gray-900">{customer.customer_name}</div>
        {customer.customer_url && (
          <a
            href={customer.customer_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary-600 hover:underline"
          >
            {new URL(customer.customer_url).hostname}
          </a>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {customer.products_contracted.join(', ') || '-'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">{firstAnnounced}</td>
      <td className="px-4 py-3 text-sm text-gray-600">{customer.customer_locations || '-'}</td>
      <td className="px-4 py-3">
        <ConfidenceBadge dataQuality={customer.data_quality} />
      </td>
    </tr>
  );
}

// Loading skeleton
function LoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="h-4 bg-gray-200 rounded w-1/4"></div>
      <div className="space-y-2">
        <div className="h-20 bg-gray-200 rounded"></div>
        <div className="h-20 bg-gray-200 rounded"></div>
        <div className="h-20 bg-gray-200 rounded"></div>
      </div>
    </div>
  );
}

// Empty state
function EmptyState({ message }: { message: string }) {
  return (
    <div className="text-center py-8 text-gray-500">
      <p>{message}</p>
    </div>
  );
}

export function CustomersTab({ companyId }: CustomersTabProps) {
  const [data, setData] = useState<CustomerTabResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await getCustomerTabData(companyId);
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch customer data'));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportCustomersCSV(companyId);
    } catch {
      // Export failed - user can retry
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-8" data-testid="customers-tab">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <LoadingSkeleton />
          <LoadingSkeleton />
        </div>
        <LoadingSkeleton />
        <LoadingSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-gray-500" data-testid="customers-tab">
        <p>Failed to load customer data. Please try again.</p>
      </div>
    );
  }

  const wins = data?.wins || [];
  const losses = data?.losses || [];
  const topCustomers = data?.top_customers || [];
  const allCustomers = data?.customers || [];
  const totalCount = data?.total_count || 0;

  return (
    <div className="space-y-8" data-testid="customers-tab">
      {/* Wins and Losses Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Recent Wins */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Recent Wins
            <span className="ml-2 text-sm font-normal text-gray-500">(Last 5)</span>
          </h3>
          {wins.length === 0 ? (
            <EmptyState message="No recent customer wins found" />
          ) : (
            <div className="space-y-3" data-testid="wins-section">
              {wins.map((win) => (
                <WinCard key={win.id} win={win} />
              ))}
            </div>
          )}
        </div>

        {/* Recent Losses */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Recent Losses
            <span className="ml-2 text-sm font-normal text-gray-500">(Last 5)</span>
          </h3>
          {losses.length === 0 ? (
            <EmptyState message="No recent customer losses found" />
          ) : (
            <div className="space-y-3" data-testid="losses-section">
              {losses.map((loss) => (
                <LossCard key={loss.id} loss={loss} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Top 10 Customers */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Top 10 Customers
          <span className="ml-2 text-sm font-normal text-gray-500">(by combined rank)</span>
        </h3>
        {topCustomers.length === 0 ? (
          <EmptyState message="No customer data available" />
        ) : (
          <div
            className="overflow-x-auto rounded-lg border border-gray-200"
            data-testid="top-customers-table"
          >
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                    Rank
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Products
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Since
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Locations
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Confidence
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {topCustomers.map((customer, index) => (
                  <CustomerRow key={customer.id} customer={customer} rank={index + 1} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* All Customers */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            All Customers
            <span className="ml-2 text-sm font-normal text-gray-500">({totalCount} total)</span>
          </h3>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="export-csv-button"
          >
            {exporting ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Exporting...
              </>
            ) : (
              <>
                <svg
                  className="-ml-1 mr-2 h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                Export CSV
              </>
            )}
          </button>
        </div>
        {allCustomers.length === 0 ? (
          <EmptyState message="No customers found. Customer data will be populated through KB builds." />
        ) : (
          <div
            className="overflow-x-auto rounded-lg border border-gray-200"
            data-testid="all-customers-table"
          >
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Products
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Since
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Locations
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Confidence
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {allCustomers.map((customer) => (
                  <CustomerRow key={customer.id} customer={customer} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
